<script lang="ts">
  import { clickOutside } from '$src/utils/clickOutside.util';
  import { createEventDispatcher } from 'svelte';
  import Search from '$icons/SearchIcon.svelte';

  type staticOption =
    | string
    | {
        option: string;
        action: () => void;
      };

  interface staticOptions {
    [category: string]: staticOption[];
  }

  export let typingSearch: string;
  export const searchTarget: string = 'Posts';
  export const loading: boolean = false;
  export let inputHTML: HTMLInputElement = null;
  //optional
  export let postPeopleSwitch = false; // 1
  export let searchIcon = true;
  export let selected = false;
  export let dropDown = false; // 2
  export let firstLine = true; // 2
  export let typingOptions: string[] = []; // 2  note: only visible when at least 1 character is typed
  export let staticOptions: staticOptions = { nocategory: [] }; // 2  note: always visible  note2: category "nocategory" will be displayed first in static section and there will be no category headline
  //CSS
  export let inputClass = '';
  export let placeholder = 'Search';

  const dispatch = createEventDispatcher();
  let dropdownOpen = false;
  let hoveredOption = 0;
  let lines: staticOption[] = [];
  let categories: string[] = [];
  let mouseHovering = false;
  let allowTypingOptions = typingSearch ? true : false;

  $: if (!typingSearch) allowTypingOptions = false;

  $: if (dropDown) {
    categories = [];
    if (typingSearch) {
      if (firstLine && hoveredOption == 0 && !mouseHovering && allowTypingOptions) {
        lines = [typingSearch];
      } else if (firstLine && allowTypingOptions) {
        lines = [lines[0]];
      } else {
        lines = [];
      }
    } else {
      lines = [];
    }
    if (typingSearch && allowTypingOptions) lines = [...lines, ...typingOptions];
    let n = lines.length;
    let staticOptionsList = [];
    for (const [key, value] of Object.entries(staticOptions)) {
      if (key == 'nocategory') {
        lines = [...lines, ...value];
      } else {
        categories[n] = key;
        staticOptionsList = [...staticOptionsList, ...value];
      }
      n = n + value.length;
    }
    lines = [...lines, ...staticOptionsList];
  }

  let timeout: NodeJS.Timeout;
  let searching = false;

  function debounce(e) {
    searching = true;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => enteredValue(e), 200);
  }

  export let enteredValue = event => {
    mouseHovering = false;
    if (event.keyCode == 38) {
      // Up
      event.preventDefault();
      if (hoveredOption != 0) {
        hoveredOption = hoveredOption - 1;
        typingSearch =
          typeof lines[hoveredOption] == 'object'
            ? (lines[hoveredOption] as any).option
            : lines[hoveredOption];
      }
    } else if (event.keyCode == 40 && lines.length != 0 && hoveredOption + 1 <= lines.length) {
      // Down
      if (hoveredOption + 1 != lines.length) {
        hoveredOption = hoveredOption + 1;
        typingSearch =
          typeof lines[hoveredOption] == 'object'
            ? (lines[hoveredOption] as any).option
            : lines[hoveredOption];
      }
    } else if (event.keyCode == 13) {
      // Enter
      dropdownOpen = false;
      if (typeof lines[hoveredOption] == 'object') {
        (lines[hoveredOption] as any).action();
      } else {
        event.preventDefault();
        dispatch('enter');
      }
    } else if (event.keyCode == 27) {
      // Esc
      dropdownOpen = false;
    } else if (event.keyCode == 8 && typingSearch.length <= 1) {
      // Backspace
      typingSearch = '';
    } else {
      allowTypingOptions = true;
      hoveredOption = 0;
    }
  };

  const dropdownItemClick = clickedText => {
    typingSearch = clickedText;
    dropdownOpen = false;
    dispatch('enter');
  };

  const click = () => {
    if (!dropdownOpen) {
      inputHTML.select();
      dropdownOpen = true;
    }
  };

  const focus = () => {
    inputHTML.select();
  };

  const handleKeyDown = event => {
    if (event.keyCode == 75 && event.ctrlKey) {
      event.preventDefault();
      inputHTML.focus();
      inputHTML.select();
      dropdownOpen = true;
    }
  };
</script>

<svelte:window on:keydown={handleKeyDown} />

<div
  class={`${
    selected ? '--input-outline' : ''
  } bg-white flex w-full h-12 border border-garden-grey-300 rounded-14 divide-x justify-between overflow-visible min-w-[350px] ${
    $$props.class
  }`}
>
  <div
    class="w-full h-full relative rounded-tr-none"
    class:dropdown-open={dropDown && dropdownOpen}
    class:rounded-b-10={dropDown && dropdownOpen && !postPeopleSwitch && !typingSearch}
    class:rounded-bl-10={dropDown && dropdownOpen && postPeopleSwitch && !typingSearch}
    class:rounded-tl-10={dropDown && dropdownOpen && postPeopleSwitch}
    class:rounded-t-10={dropDown && dropdownOpen && !postPeopleSwitch}
    use:clickOutside
    on:click_outside={() => (dropdownOpen = false)}
    on:focus={focus}
  >
    <button
      class="w-full h-full cursor-text overflow-scroll scrollbar-hide flex items-center"
      on:keydown
      on:click={click}
    >
      <div class="flex items-center gap-2 w-full">
        <div class="inline-block pl-3">
          {#if searchIcon}
            <Search strokeWidth={1.5} size={20} class="stroke-garden-grey-600" />
          {/if}
        </div>

        <input
          type="text"
          {placeholder}
          bind:this={inputHTML}
          bind:value={typingSearch}
          on:keyup={debounce}
          on:focus
          class="break-words placeholder-garden-grey-400 block w-full h-full text-sm align-middle bg-transparent outline-none relative top-[0.065rem] selection:bg-brand-green-50 {inputClass} font-switzer"
        />

        <slot name="right-slot" />
      </div>
    </button>

    {#if dropDown && dropdownOpen}
      <button
        class="w-full pt-2 relative -top-3 text-sm bg-white outline outline-white outline-2 rounded-b-10 hover:cursor-pointer"
        on:mouseleave={() => (hoveredOption = 0)}
      >
        {#each lines as option, i}
          {@const lineNumber = i}
          {@const categoryNumber =
            allowTypingOptions && typingSearch ? i - typingOptions.length : i}
          {#if categories[categoryNumber]}
            <div
              class="pr-5 break-all overflow-wrap min-h-[2rem] pl-[2.4rem] last:rounded-b-10 outline outline-transparent outline-2 flex items-center text-color-slate-2 text-xs font-bold"
            >
              {categories[categoryNumber]}
            </div>
          {/if}
          <button
            class="pr-5 break-all overflow-wrap min-h-[2rem] pl-[2.4rem] last:rounded-b-10 outline outline-transparent outline-2 flex items-center"
            class:bg-slate-50={hoveredOption == lineNumber}
            class:outline-slate-50={hoveredOption == lineNumber}
            on:keydown
            on:mouseenter={() => {
              hoveredOption = lineNumber;
              mouseHovering = true;
            }}
            on:click={() =>
              typeof option == 'object' ? option.action() : dropdownItemClick(option)}
          >
            {typeof option == 'object' ? option.option : option}
          </button>
        {/each}
      </button>
    {/if}
  </div>
</div>

<style lang="postcss">
  .--input-outline {
    box-shadow:
      0px 0px 0px 3px #ecefff,
      1px 1px 2px 0px rgba(0, 0, 0, 0.07);
  }
  .dropdown-open {
    @apply bg-white outline outline-white outline-2;
    @apply drop-shadow-2xl;
  }
</style>
